import React, { useState, useEffect } from "react";
import { useLanguage } from "./LanguageContext";
import { useScrollAnimation } from "./useScrollAnimation";
import SandRevealImg from "./SandRevealImg";
import "./Apps.css";

const GITHUB_URL =
  "https://raw.githubusercontent.com/Alfaghiri/website/master/apps.json";

const imgBase = "https://hoope.me/img/";

const CATEGORY_IMAGES = {
  Programming:    { img: "prog.svg",  alt: "Programming" },
  WebProgramming: { img: "web.svg",   alt: "Web Programming" },
  Adobe:          { img: "adobe.svg", alt: "Adobe" },
  "2D":           { img: "2d.svg",    alt: "2D" },
  "3D":           { img: "3d.svg",    alt: "3D" },
  Video:          { img: "video.svg", alt: "Video" },
  Vedio:          { img: "video.svg", alt: "Video" },
  Office:         { img: "mso.svg",   alt: "Office" },
  Automation:     { img: "nr.svg",    alt: "Automation" },
  DevOps:         { img: "po.svg",    alt: "DevOps" },
  Typesetting:    { img: "latex.svg", alt: "Typesetting" },
};

const groupByCategory = (items) => {
  const order = [];
  const map   = {};
  for (const item of items) {
    const cat = item.category || "Other";
    if (!map[cat]) { map[cat] = []; order.push(cat); }
    map[cat].push(item);
  }
  return order.map((cat) => ({ category: cat, items: map[cat] }));
};

/* ── Single skill card with staggered fade-in ── */
function SkillCard({ item, delay, imgBase, getDesc }) {
  const [ref, visible] = useScrollAnimation(0.1);

  return (
    <div
      ref={ref}
      className={`skill-card ${visible ? "card-visible" : "card-hidden"}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {/* Sand-grain reveal on the icon */}
      <SandRevealImg
        src={imgBase + item.imageSrc}
        alt={item.title}
        imgClassName="skill-icon"
        size={90}
      />
      <p>
        <strong>{item.title}</strong> {getDesc(item)}
      </p>
    </div>
  );
}

/* ── Fallback card (LanguageContext hardcoded data) ── */
function FallbackCard({ item, delay, imgBase }) {
  const [ref, visible] = useScrollAnimation(0.1);

  return (
    <div
      ref={ref}
      className={`skill-card ${visible ? "card-visible" : "card-hidden"}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      <SandRevealImg
        src={imgBase + item.img}
        alt={item.title}
        imgClassName="skill-icon"
        size={90}
      />
      <p>
        <strong>{item.title}</strong> {item.description}
      </p>
    </div>
  );
}

/* ── Category section with header SVG also sand-revealed ── */
function CategorySection({ header, items, getDesc }) {
  return (
    <section className="skills-section">
      {header && (
        <div className="skills-category-header">
          <SandRevealImg
            src={imgBase + header.img}
            alt={header.alt}
            imgClassName="category-icon"
            size={120}
          />
        </div>
      )}
      <div className="skills-grid">
        {items.map((item, i) => (
          <SkillCard
            key={i}
            item={item}
            delay={i * 70}
            imgBase={imgBase}
            getDesc={getDesc}
          />
        ))}
      </div>
    </section>
  );
}

/* ── Main component ── */
const Apps = () => {
  const { lang, skills: fallbackSkills } = useLanguage();
  const [rawItems, setRawItems] = useState(null);
  const [error,    setError]    = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRawItems(null);
    setError(false);

    fetch(GITHUB_URL)
      .then((r) => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((d)  => { if (!cancelled) setRawItems(d); })
      .catch(()  => { if (!cancelled) setError(true); });

    return () => { cancelled = true; };
  }, []);

  const getDesc = (item) => {
    if (lang === "de" && item.description_de) return item.description_de;
    if (lang === "en" && item.description_en) return item.description_en;
    return item.description || "";
  };

  if (!error && rawItems === null) {
    return (
      <div className="skills-wrapper" style={{ textAlign: "center", padding: "60px 0", color: "#c8e0ea" }}>
        <p>Laden …</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skills-wrapper">
        {fallbackSkills.map((cat, ci) => (
          <section key={ci} className="skills-section">
            <div className="skills-category-header">
              <SandRevealImg
                src={imgBase + cat.categoryImg}
                alt={cat.categoryAlt}
                imgClassName="category-icon"
                size={120}
              />
            </div>
            <div className="skills-grid">
              {cat.items.map((item, i) => (
                <FallbackCard key={i} item={item} delay={i * 70} imgBase={imgBase} />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  const grouped = groupByCategory(rawItems);

  return (
    <div className="skills-wrapper">
      {grouped.map(({ category, items }, ci) => (
        <CategorySection
          key={ci}
          header={CATEGORY_IMAGES[category]}
          items={items}
          getDesc={getDesc}
        />
      ))}
    </div>
  );
};

export default Apps;
